/* ================================================================
   PANTHERATEC | Collections v4.0
   Sorting and real automatic infinite scroll (Shopify Section Rendering API).
   Preserves all native Shopify collection filters and their query parameters.
   ================================================================ */
(function () {
  'use strict';

  // Sorting also works after Shopify replaces the grid with filtered results.
  if (!window.__ptcCollectionSortBound) {
    window.__ptcCollectionSortBound = true;
    document.addEventListener('change', function (event) {
      var select = event.target.closest('[data-ptc-sort]');
      if (!select) return;
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  }

  if (window.__ptcInfiniteScrollBound) return;
  window.__ptcInfiniteScrollBound = true;

  var selector = '[data-ptc-infinite]';
  var observer = null;
  var watched = new WeakSet();

  function resolvedPageUrl(marker) {
    // paginate.next.url is supplied by Shopify. Keep the current filters/sort
    // even if a custom URL accidentally omitted one of the query parameters.
    var url = new URL(marker.dataset.ptcNextUrl, window.location.href);
    var current = new URL(window.location.href);
    current.searchParams.forEach(function (value, key) {
      if (key === 'page' || key === 'section_id' || key === 'sections') return;
      if (!url.searchParams.has(key)) url.searchParams.append(key, value);
    });
    return url;
  }

  function updateCount(marker, grid) {
    if (!marker || !grid) return;
    var total = Number(marker.dataset.ptcTotal);
    if (!Number.isFinite(total) || total <= 0) return;
    var countElement = marker.closest('.ptc-main-section').querySelector('.ptc-toolbar__count');
    if (!countElement) return;
    var shown = grid.querySelectorAll('[data-ptc-product-id]').length;
    countElement.textContent = shown < total
      ? shown + ' von ' + total + ' Produkten'
      : total + ' Produkte';
  }

  function state(marker, value) {
    marker.dataset.state = value;
    var isError = value === 'error';
    var error = marker.querySelector('.ptc-infinite__error');
    var retry = marker.querySelector('.ptc-infinite__retry');
    var fallback = marker.querySelector('.ptc-infinite__fallback-link');
    if (error) error.hidden = !isError;
    if (retry) retry.hidden = !isError;
    if (fallback) fallback.hidden = !isError;
  }

  async function fetchPage(marker) {
    if (!marker || !marker.isConnected || marker.dataset.ptcLoading === 'true') return;
    if (!marker.dataset.ptcNextUrl) return;

    var container = marker.closest('#ProductGridContainer');
    var grid = container && container.querySelector('motion-list.ptc-product-grid');
    if (!grid) return;

    var pageUrl = resolvedPageUrl(marker);
    var sectionId = marker.dataset.ptcSectionId;
    // Preserve the locale and all active Shopify storefront filter parameters.
    var sectionUrl = new URL(pageUrl.toString());
    sectionUrl.searchParams.set('section_id', sectionId);

    marker.dataset.ptcLoading = 'true';
    state(marker, 'loading');
    if (observer) observer.unobserve(marker);

    try {
      var response;
      var page = null;
      var nextGrid = null;
      // The section endpoint is much smaller than a complete storefront page.
      // If it is unavailable, automatically try the ordinary collection URL.
      try {
        response = await fetch(sectionUrl.toString(), {
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html' }
        });
        if (response.ok) {
          page = new DOMParser().parseFromString(await response.text(), 'text/html');
          nextGrid = page.querySelector('.ptc-main-section #ProductGridContainer motion-list.ptc-product-grid');
        }
      } catch (sectionError) {
        console.warn('[Pantheratec] Section request unavailable; trying full page.', sectionError);
      }
      if (!nextGrid) {
        response = await fetch(pageUrl.toString(), {
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html' }
        });
        if (!response.ok) throw new Error('Shopify full page response: ' + response.status);
        page = new DOMParser().parseFromString(await response.text(), 'text/html');
        nextGrid = page.querySelector('.ptc-main-section #ProductGridContainer motion-list.ptc-product-grid');
      }
      if (!nextGrid) throw new Error('Product grid not found in subsequent page');
      if (!marker.isConnected || !grid.isConnected) return; // Filter changed mid-request.

      var existing = new Set();
      grid.querySelectorAll('[data-ptc-product-id]').forEach(function (item) {
        existing.add(item.getAttribute('data-ptc-product-id'));
      });
      var fragment = document.createDocumentFragment();
      var added = 0;
      Array.from(nextGrid.children).forEach(function (item) {
        var pid = item.getAttribute('data-ptc-product-id');
        if (pid && existing.has(pid)) return;
        if (pid) existing.add(pid);
        var card = document.importNode(item, true);
        // Newly inserted cards must not remain invisible due to theme reveal animations.
        card.style.setProperty('opacity', '1', 'important');
        card.style.setProperty('visibility', 'visible', 'important');
        fragment.appendChild(card);
        if (pid) added++;
      });
      if (added === 0) throw new Error('No new products returned (page may be duplicated)');

      grid.appendChild(fragment);
      updateCount(marker, grid);

      var follow = page.querySelector('.ptc-main-section #ProductGridContainer [data-ptc-infinite]');
      if (follow && follow.dataset.ptcNextUrl) {
        var oldNext = marker.dataset.ptcNextUrl;
        var newNext = follow.dataset.ptcNextUrl;
        if (new URL(oldNext, location.href).toString() === new URL(newNext, location.href).toString()) {
          // Avoid a loop if a CDN or another app serves the same page repeatedly.
          throw new Error('Pagination did not advance');
        }
        marker.dataset.ptcNextUrl = newNext;
        var fallback = marker.querySelector('.ptc-infinite__fallback-link');
        if (fallback) fallback.href = newNext;
        state(marker, 'idle');
      } else {
        // Shopify returned the final page: remove the sentinel, not the product grid.
        marker.remove();
        return;
      }
    } catch (error) {
      console.error('[Pantheratec] Infinite scroll:', error);
      if (marker.isConnected) {
        var fallback = marker.querySelector('.ptc-infinite__fallback-link');
        if (fallback) fallback.href = pageUrl.toString();
        state(marker, 'error');
      }
    } finally {
      delete marker.dataset.ptcLoading;
      if (marker.isConnected && marker.dataset.state === 'idle' && observer) {
        observer.observe(marker);
      }
    }
  }

  function initMarker(marker) {
    if (watched.has(marker)) return;
    watched.add(marker);
    marker.querySelector('.ptc-infinite__retry')?.addEventListener('click', function () {
      fetchPage(marker);
    });
    var grid = marker.closest('#ProductGridContainer')?.querySelector('motion-list.ptc-product-grid');
    updateCount(marker, grid);
    if (observer) observer.observe(marker);
  }

  function init(root) {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    if (root.matches?.(selector)) initMarker(root);
    root.querySelectorAll?.(selector).forEach(initMarker);
  }

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.target.dataset.state !== 'error') {
          fetchPage(entry.target);
        }
      });
    }, { rootMargin: '650px 0px', threshold: 0 });
  } else {
    // Very old browsers: check the sentinel when the shopper scrolls/resizes.
    var fallbackCheck = function () {
      document.querySelectorAll(selector).forEach(function (marker) {
        if (marker.getBoundingClientRect().top < window.innerHeight + 650 &&
            marker.dataset.state !== 'error') fetchPage(marker);
      });
    };
    window.addEventListener('scroll', fallbackCheck, { passive: true });
    window.addEventListener('resize', fallbackCheck);
    window.addEventListener('load', fallbackCheck);
  }

  function start() {
    init(document);
    // Shopify's facet AJAX can replace ProductGridContainer. Detect new
    // sentinels without re-registering duplicate observers or click handlers.
    if (!document.body) return;
    var mutationObserver = new MutationObserver(function (changes) {
      changes.forEach(function (change) {
        change.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) init(node);
        });
      });
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
